import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DataTableComponent from 'app_components/commons/dataTable';
import { IUserCertification } from 'app_types/campus/user/userInfoTypes';
import { AiOutlineEye } from 'react-icons/ai';
import { Spinner } from 'react-bootstrap';
import { FaFacebook, FaFileDownload, FaLinkedin, FaTwitter } from 'react-icons/fa';
import { FiExternalLink } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import moment from 'moment';
import CertificateService from 'app_services/user/certificateService';
import TableItemsPaginator from 'app_components/modules/TableItemsPaginator/TableItemsPaginator';
import StudentDashboardCustomTable from '../StudentDashboardCustomTable/StudentDashboardCustomTable';
import useWindowWidth from 'app_hooks/useWindowWidth';
import AccordionTable from 'app_components/modules/AccordionTable/AccordionTable';
import TextComponent from 'app_components/commons/TextComponent/TextComponent';
import ButtonComponent from 'app_components/commons/Button/ButtonComponent';
import { ICertificatePaymentPreview } from 'app_types/user/certificateTypes';
import CertificatePaymentPreviewModal from 'app_components/modules/CertificatePaymentPreviewModal/CertificatePaymentPreviewModal';
import { useSelector } from 'react-redux';
import { CombinedReducers } from 'app_events/index';

const ITEMS_PER_PAGE = 20
const MOBILE_BREAKPOINT = 800

interface IStudentDashboardCertificationsTableProps {
  certificationsToRender: IUserCertification[]
}

const StudentDashboardCertificationsTable: FC<IStudentDashboardCertificationsTableProps> = ({
  certificationsToRender,
}) => {
  const { t } = useTranslation()
  const [certifications, setCertifications] = useState<IUserCertification[]>([])
  const certificateService = useMemo(() => new CertificateService(), [])
  const [paymentPreview, setPaymentPreview] = useState<ICertificatePaymentPreview>()
  const { windowWidth } = useWindowWidth()
  const user = useSelector((state: CombinedReducers) => state.auth?.user?.data)
  const [pdfDownload, setPdfDownload] = useState<{
    status: 'initial' | 'loaded' | 'loading',
    file: any | null
  }>({ status: 'initial', file: null })
  const [pdfPreview, setPdfPreview] = useState<{
    status: 'initial' | 'loaded' | 'loading',
    file: any | null
  }>({ status: 'initial', file: null })

  const handleDownloadFile = useCallback(async (status: boolean, certificate?: IUserCertification) => {
    if (status === true && certificate) {
      setPdfDownload({ status: 'loading', file: null })
      if (certificate.pdfPath) {
        certificate.downloadDate = moment().format('YYYY-MM-DD HH:mm:ss').replace(' ', 'T');
        await certificateService.updateCertificationFirstDownloadDate(certificate);
        setPdfDownload({ status: 'loaded', file: certificate.pdfPath })
        window.open(certificate.pdfPath, '_blank');
      } else {
        const responsePreview = await certificateService.loadPreview(certificate.hash, {
          format: 2,
          template: 1,
          updateCertificate: true,
          certificate_queue: certificate._id
        })
        if (responsePreview && responsePreview.certificate?.pdfPath) {
          certificate.downloadDate = moment().format('YYYY-MM-DD HH:mm:ss').replace(' ', 'T');
          await certificateService.updateCertificationFirstDownloadDate(certificate);
          setPdfDownload({ status: 'loaded', file: responsePreview.certificate?.pdfPath })
          certificate.pdfPath = responsePreview.certificate?.pdfPath
          window.open(responsePreview.certificate?.pdfPath, '_blank');
        } else {
          setPdfDownload({ status: 'initial', file: null })
        }
      }
    } else {
      setPdfDownload({ status: 'initial', file: null })
    }
  }, [certificateService])

  const getCertifications = useCallback(async () => {
    let sortedCertifications = []
    if (certificationsToRender?.length) {
      sortedCertifications = certificationsToRender.sort((a: any, b: any) => {
        const date1 = new Date(a.date)
        const date2 = new Date(b.date)
        if (date1 < date2) {
          return 1;
        }
        if (date1 > date2) {
          return -1;
        }
        return 0
      }).map((certificate: any) => {
        return {
          ...certificate,
          status: certificate?.status === 'Complete' ? 'Emitido' : certificate?.status === 'Error' ? 'Generando...' : certificate?.wasPaid ? 'Generando...' : 'Sin emitir',
          component_render_download: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
              {certificate?.needPayment && !certificate?.wasPaid && !certificate?.transactionIsPending && (
                <ButtonComponent
                  onClick={() => setPaymentPreview({
                    certificate,
                    docNumber: user?.profile?.doc_number as string,
                    fullName: `${user?.profile?.first_name ? user?.profile?.first_name : ''} ${user?.profile?.last_name ? user?.profile?.last_name : ''}`
                  })}
                  variant="tertiary"
                  fontSize="12px"
                  padding="5px 15px"
                >
                  Adquirir Certificado
                </ButtonComponent>
              )}
              {certificate?.needPayment && certificate?.transactionIsPending && (
                <ButtonComponent
                  fontSize="12px"
                  padding="5px 15px"
                  disabled={!certificate?.transactionURL}
                  onClick={() => window.open(certificate?.transactionURL, '_blank', 'noreferrer')}
                  textDecoration={certificate?.transactionURL ? 'underline' : undefined}
                >
                  {certificate?.transactionURL ? 'Continuar transacción' : 'Transacción en proceso'}
                </ButtonComponent>
              )}
              <button
                className='btn btn-link btn-sm'
                data-tip={t('modules.modal_user_certifications.actions.download')}
                onClick={() => handleDownloadFile(true, certificate)}
                disabled={pdfDownload.status === 'loading' || (certificate?.needPayment && !certificate?.wasPaid) || certificate?.transactionIsPending}
              >
                {(pdfDownload.status === 'loading' || (certificate?.wasPaid && certificate?.status !== 'Complete')) ? (
                  <Spinner className='spinner-loading' animation='border' size='sm' />
                ) : (
                  <FaFileDownload size="20px" color='var(--primary)' />
                )}
              </button>
            </div>
          )
        }
      })
    }
    setCertifications(sortedCertifications)
  }, [certificationsToRender, pdfDownload, handleDownloadFile, t])

  useEffect(() => {
    getCertifications()
  }, [getCertifications])

  const checkLink = (certification: IUserCertification) => {
    let link = undefined
    if (certification?.certificate?.source === 'acredita') {
      if (certification?.urlCredencial) {
        link = certification?.urlCredencial
      } else if (certification?.urlDownload) {
        link = certification?.urlDownload
      }
    } else {
      link = `https%3A%2F%2Fwww.huelladeconfianza.com%2Fcertificado%2F${certification.hash}%2F`
    }
    return link
  }

  const shareTwitter = (certification: IUserCertification) => {
    const link = checkLink(certification)
    if (link) {
      window.open(`https://twitter.com/intent/tweet?hashtags=HuellaDeConfianza&text=Ya+puedes+consultar+mi+certificado&url=${link}&via=icontec`, '_blank');
    }
  }

  const shareFacebook = (certification: IUserCertification) => {
    const link = checkLink(certification)
    if (link) {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${link}`, '_blank');
    }
  }

  const shareLinkedin = (certificate: IUserCertification) => {
    const link = checkLink(certificate)
    let url = "https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&";
    url += `name=${certificate.title}&`
    url += "organizationId=1298608&";
    url += "issueYear=" + certificate?.date.substring(0, 4) + "&";
    url += "issueMonth=" + certificate?.date.substring(5, 7) + "&";
    url += `certUrl=${link}/&`;
    url += "certId=" + certificate.hash;
    const url_encoded = encodeURI(url);
    window.open(url_encoded, '_blank');
  }

  const handlePreviewFile = async (status: boolean, certificate?: IUserCertification) => {
    if (status === true && certificate) {
      setPdfPreview({ status: 'loading', file: null })
      if (certificate.pdfPath) {
        setPdfPreview({ status: 'loaded', file: certificate.pdfPath })
        window.open(certificate.pdfPath, '_blank', 'noreferrer')
      } else {
        // @ts-ignore
        const responsePreview = await certificateService.loadPreview(certificate?.certificate?.hash, {
          format: 1,
          template: 1,
          updateCertificate: true,
          certificate_queue: certificate._id
        })
        if (responsePreview && responsePreview.certificate?.pdfPath) {
          setPdfPreview({ status: 'loaded', file: responsePreview.certificate?.pdfPath })
          certificate.pdfPath = responsePreview.certificate?.pdfPath
          window.open(certificate.pdfPath, '_blank', 'noreferrer')
        } else {
          setPdfPreview({ status: 'initial', file: null })
        }
      }
    } else {
      setPdfPreview({ status: 'initial', file: null })
    }
  }

  const headerKeys = useMemo(() => [
    `${t('modules.modal_user_certifications.table.title')}`,
    `${t('modules.modal_user_certifications.table.download_certificate')}`,
    `${t('modules.modal_user_certifications.table.date')}`,
    `${t('modules.modal_user_certifications.table.status')}`,
  ], [t])

  const keys = useMemo(() => [
    'title',
    'component_render_download',
    'date',
    'status',
  ], [])

  const renderLinkCertificate = (certification: IUserCertification) => {
    let link = undefined
    if (certification?.urlCredencial) {
      link = certification.urlCredencial
    } else if (certification.urlDownload) {
      link = certification.urlDownload
    }
    if (link) {
      return (
        <Link
          to={{ pathname: `${link}` }}
          target='_blank'
        >
          <button
            className='btn btn-link btn-sm'
            data-tip={t('modules.modal_user_certifications.actions.emitter_validation')}
          >
            <FiExternalLink size="20px" color='var(--primary)' />
          </button>
        </Link>
      )
    }
    return null;
  }

  const getActionsTable = (certification: IUserCertification) => (
    <div style={{ display: 'flex', justifyContent: 'center', width: 'fit-content' }}>
      <button
        className='btn btn-link btn-sm'
        data-tip={t('modules.modal_user_certifications.actions.preview')}
        onClick={() => handlePreviewFile(true, certification)}
        disabled={pdfPreview.status === 'loading'}
      >
        <AiOutlineEye size="20px" color='var(--primary)' />
      </button>
      {renderLinkCertificate(certification)}

      <button
        className='btn btn-link btn-sm'
        data-tip={t('modules.modal_user_certifications.actions.share_twitter')}
        onClick={() => shareTwitter(certification)}
        disabled={pdfDownload.status === 'loading'}
      >
        {pdfDownload.status === 'loading' ? (
          <Spinner className='spinner-loading' animation='border' size='sm' />
        ) : (
          <FaTwitter size="20px" color='var(--primary)' />
        )}
      </button>
      <button
        className='btn btn-link btn-sm'
        data-tip={t('modules.modal_user_certifications.actions.share_facebook')}
        onClick={() => shareFacebook(certification)}
        disabled={pdfDownload.status === 'loading'}
      >
        {pdfDownload.status === 'loading' ? (
          <Spinner className='spinner-loading' animation='border' size='sm' />
        ) : (
          <FaFacebook size="20px" color='var(--primary)' />
        )}
      </button>
      <button
        className='btn btn-link btn-sm'
        data-tip={t('modules.modal_user_certifications.actions.share_linkedin')}
        onClick={() => shareLinkedin(certification)}
        disabled={pdfDownload.status === 'loading'}
      >
        {pdfDownload.status === 'loading' ? (
          <Spinner className='spinner-loading' animation='border' size='sm' />
        ) : (
          <FaLinkedin size="20px" color='var(--primary)' />
        )}
      </button>

    </div>
  )

  return (
    <>
      <TableItemsPaginator items={certifications} perPage={ITEMS_PER_PAGE}>
        {(itemsToRender) => (
          windowWidth && windowWidth > MOBILE_BREAKPOINT ? (
            <StudentDashboardCustomTable id="student_dashboard_certifications_table">
              <DataTableComponent
                data={itemsToRender}
                headerKeys={headerKeys}
                keys={keys}
                detailsKey={[]}
                onModal={null}
                actionsColTitle="Acciones"
                hideTooltip
                headerStyles={[ { key: 'actions', style: { width: '205px' } } ]}
                rowButtons={(certification: IUserCertification) => getActionsTable(certification)}
                tooltipKeys={['title']}
              />
            </StudentDashboardCustomTable>
          ) : (
            <AccordionTable
              id={`student_dashboard_certifications_table-accordion-table`}
              items={itemsToRender}
              renderContent={(item) => (
                <div>
                  <ul>
                    <li>
                      <TextComponent>
                        <strong>{t('modules.modal_user_certifications.table.title')}:</strong> {item.title}
                      </TextComponent>
                    </li>
                    <li>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <TextComponent>
                          <strong>{t('modules.modal_user_certifications.table.download_certificate')}:</strong>
                        </TextComponent>
                        {item.component_render_download}
                      </div>
                    </li>
                    <li>
                      <TextComponent>
                        <strong>{t('modules.modal_user_certifications.table.date')}:</strong> {item.date}
                      </TextComponent>
                    </li>
                    <li>
                      <TextComponent>
                        <strong>{t('modules.modal_user_certifications.table.status')}:</strong> {item.status}
                      </TextComponent>
                    </li>
                    <li>
                      <div>
                        <TextComponent>
                          <strong>Acciones:</strong>
                        </TextComponent>
                        {getActionsTable(item)}
                      </div>
                    </li>
                  </ul>
                </div>
              )}
              renderHeader={(item) => (
                <TextComponent fontSize="14px" elipsis>
                  {item.title}
                </TextComponent>
              )}
            />
          )
        )}
      </TableItemsPaginator>
      <CertificatePaymentPreviewModal
        open={!!paymentPreview}
        onClose={() => setPaymentPreview(undefined)}
        data={paymentPreview}
      />
    </>
  )
}

export default StudentDashboardCertificationsTable
